import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import Client from '../Client';
import Config from '../ClientConfig';

const STATUS_HOURS = 48;
const DIALOG_LIMIT = 200;
const LOG_ROW_HEIGHT = 76;
const logCellSx = {
  height: LOG_ROW_HEIGHT,
  maxHeight: LOG_ROW_HEIGHT,
  py: 0.75,
  verticalAlign: 'top',
  overflow: 'hidden',
};

function ClampedText({ children, lines = 2, title }) {
  return (
    <Typography
      variant="body2"
      title={title}
      sx={{
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lines,
        overflow: 'hidden',
        wordBreak: 'break-word',
        lineHeight: 1.35,
      }}
    >
      {children}
    </Typography>
  );
}

export function buildSocrateErrorsQuery(hours, limit = DIALOG_LIMIT, now = Date.now()) {
  return {
    source: 'socrate',
    event_type_prefix: 'socrate_material_',
    since: new Date(now - (hours * 60 * 60 * 1000)).toISOString(),
    limit,
  };
}

export function socrateFlowLabel(eventType) {
  const value = String(eventType || '');
  const isImport = value.includes('_import_');

  if (value.includes('material_receipt')) {
    return isImport ? 'Receipt import' : 'Material receipt';
  }

  if (value.includes('material_move')) {
    return isImport ? 'Movement import' : 'Material movement';
  }

  return 'Socrate material';
}

function documentLabel(row) {
  if (row.order_code) return row.order_code;

  const match = String(row.message || '').match(/#([^\]]+)\]/);
  if (match) return match[1].trim();

  return row.object_iref || '—';
}

function productRelations(message) {
  const relations = [];
  const expression = /product\(rel=['"]([^'"]+)['"]\)/gi;
  let match = expression.exec(String(message || ''));

  while (match) {
    if (!relations.includes(match[1])) relations.push(match[1]);
    match = expression.exec(String(message || ''));
  }

  return relations.length ? relations.join(', ') : '—';
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

export default class SocrateErrorsDialog extends React.Component {
  constructor(props) {
    super(props);

    this.client = props.client || new Client();
    this.client.prefix = Config.API_URL_LOGS_PLATFORM || `${Config.API_URL}platformLogs/`;
    this.statusRequest = null;
    this.dialogRequest = null;
    this.mounted = false;

    this.state = {
      open: false,
      hours: STATUS_HOURS,
      statusLoading: true,
      statusError: false,
      statusCount: 0,
      dialogLoading: false,
      dialogError: '',
      dialogCount: 0,
      logs: [],
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.loadStatus();
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.statusRequest?.abort) this.statusRequest.abort();
    if (this.dialogRequest?.abort) this.dialogRequest.abort();
  }

  requestReport = (hours, limit) => (
    this.client.getDataAsync('report/', buildSocrateErrorsQuery(hours, limit))
  );

  loadStatus = () => {
    if (this.statusRequest?.abort) this.statusRequest.abort();

    this.setState({ statusLoading: true, statusError: false });
    const request = this.requestReport(STATUS_HOURS, 1);
    this.statusRequest = request;

    Promise.resolve(request)
      .then((result) => {
        if (!this.mounted || this.statusRequest !== request) return;
        if (!result?.summary) throw new Error('Invalid platform log response.');

        this.setState({
          statusLoading: false,
          statusError: false,
          statusCount: Number(result.summary.rows) || 0,
        });
      })
      .catch(() => {
        if (!this.mounted || this.statusRequest !== request) return;
        this.setState({ statusLoading: false, statusError: true, statusCount: 0 });
      });
  };

  loadDialog = (hours = this.state.hours) => {
    if (this.dialogRequest?.abort) this.dialogRequest.abort();

    this.setState({
      dialogLoading: true,
      dialogError: '',
    });
    const request = this.requestReport(hours, DIALOG_LIMIT);
    this.dialogRequest = request;

    Promise.resolve(request)
      .then((result) => {
        if (!this.mounted || this.dialogRequest !== request) return;
        if (!result?.summary || !Array.isArray(result.logs)) {
          throw new Error('Invalid platform log response.');
        }

        this.setState({
          dialogLoading: false,
          dialogCount: Number(result.summary.rows) || 0,
          logs: result.logs,
        });
      })
      .catch(() => {
        if (!this.mounted || this.dialogRequest !== request) return;
        this.setState({
          dialogLoading: false,
          dialogError: 'Socrate errors could not be loaded. Please try again.',
        });
      });
  };

  openDialog = () => {
    this.setState({ open: true }, () => this.loadDialog());
  };

  closeDialog = () => {
    this.setState({ open: false });
  };

  changeHours = (_event, hours) => {
    if (!hours || hours === this.state.hours) return;
    this.setState({ hours }, () => this.loadDialog(hours));
  };

  refresh = () => {
    this.loadStatus();
    this.loadDialog();
  };

  renderStatusButton() {
    const { statusCount, statusError, statusLoading } = this.state;
    const hasErrors = statusCount > 0;
    const tooltip = statusError
      ? 'Socrate error status could not be checked.'
      : `${statusCount} Socrate material error${statusCount === 1 ? '' : 's'} in the last 48 hours.`;

    return (
      <Tooltip title={tooltip} arrow>
        <Button
          aria-label={statusError
            ? 'Socrate errors status unavailable'
            : `Socrate errors, ${statusCount} in the last 48 hours`}
          onClick={this.openDialog}
          size="small"
          variant="outlined"
          color={hasErrors ? 'error' : (statusError ? 'warning' : 'inherit')}
          startIcon={statusLoading
            ? <CircularProgress size={14} color="inherit" />
            : (statusError ? <WarningAmberIcon /> : <ErrorOutlineIcon />)}
          sx={{
            flexShrink: 0,
            minWidth: 0,
            mr: { xs: 0.5, sm: 1 },
            px: { xs: 0.75, sm: 1.25 },
            py: 0.45,
            textTransform: 'none',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            ...(!hasErrors && !statusError ? {
              color: 'text.secondary',
              borderColor: 'rgba(15, 23, 42, 0.22)',
            } : {}),
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Socrate errors
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Socrate
          </Box>
          {hasErrors ? ` (${statusCount})` : ''}
        </Button>
      </Tooltip>
    );
  }

  render() {
    const {
      open,
      hours,
      dialogLoading,
      dialogError,
      dialogCount,
      logs,
    } = this.state;

    return (
      <>
        {this.renderStatusButton()}

        <Dialog
          open={open}
          onClose={this.closeDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              width: {
                xs: 'calc(100vw - 16px)',
                sm: 'calc(100vw - 64px)',
                lg: 1100,
              },
              height: {
                xs: 'calc(100dvh - 16px)',
                sm: 640,
                md: 680,
              },
              maxHeight: {
                xs: 'calc(100dvh - 16px)',
                sm: 'calc(100dvh - 64px)',
              },
              m: { xs: 1, sm: 4 },
            },
          }}
        >
          <DialogTitle sx={{ pr: 6, py: 1.25 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography component="span" variant="h6" sx={{ fontWeight: 700, mr: { sm: 'auto' } }}>
                Socrate material errors
              </Typography>
              <ToggleButtonGroup
                value={hours}
                exclusive
                size="small"
                onChange={this.changeHours}
                aria-label="Socrate errors period"
                sx={{ '& .MuiToggleButton-root': { py: 0.35, px: 1.25, textTransform: 'none' } }}
              >
                <ToggleButton value={24}>24h</ToggleButton>
                <ToggleButton value={48}>48h</ToggleButton>
              </ToggleButtonGroup>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.refresh}
                disabled={dialogLoading}
              >
                Refresh
              </Button>
            </Stack>
            <Tooltip title="Close">
              <IconButton
                aria-label="Close Socrate errors"
                onClick={this.closeDialog}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: { xs: 1, sm: 2 },
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ height: 4, mb: 1, flexShrink: 0 }}>
              {dialogLoading ? <LinearProgress /> : null}
            </Box>

            {dialogError ? <Alert severity="warning">{dialogError}</Alert> : null}

            {!dialogError && !dialogLoading && logs.length === 0 ? (
              <Alert severity="success">No Socrate material errors in the last {hours} hours.</Alert>
            ) : null}

            {!dialogError && logs.length > 0 ? (
              <>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                  {dialogLoading
                    ? 'Loading recent errors…'
                    : `${dialogCount} log line${dialogCount === 1 ? '' : 's'} in the last ${hours} hours.`}
                </Typography>
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table
                    size="small"
                    stickyHeader
                    aria-label="Recent Socrate material errors"
                    sx={{ tableLayout: 'fixed', minWidth: 900 }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 150, fontWeight: 700 }}>Last seen</TableCell>
                        <TableCell sx={{ width: 170, fontWeight: 700 }}>Flow</TableCell>
                        <TableCell sx={{ width: 135, fontWeight: 700 }}>Document</TableCell>
                        <TableCell sx={{ width: 190, fontWeight: 700 }}>Product relation</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                        <TableCell align="right" sx={{ width: 95, fontWeight: 700 }}>Occurrences</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((row) => (
                        <TableRow key={row.id} hover sx={{ height: LOG_ROW_HEIGHT, maxHeight: LOG_ROW_HEIGHT }}>
                          <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap' }}>
                            {formatDateTime(row.lastSeenOn)}
                          </TableCell>
                          <TableCell sx={logCellSx}>
                            <Chip size="small" color="error" variant="outlined" label={socrateFlowLabel(row.event_type)} />
                            {row.error_code ? (
                              <Typography
                                variant="caption"
                                title={row.error_code}
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {row.error_code}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={logCellSx}>
                            <ClampedText title={documentLabel(row)}>{documentLabel(row)}</ClampedText>
                          </TableCell>
                          <TableCell sx={logCellSx}>
                            <ClampedText title={productRelations(row.message)}>
                              {productRelations(row.message)}
                            </ClampedText>
                          </TableCell>
                          <TableCell sx={logCellSx}>
                            <ClampedText lines={3} title={row.message || '—'}>
                              {row.message || '—'}
                            </ClampedText>
                          </TableCell>
                          <TableCell align="right" sx={logCellSx}>
                            {Number(row.occurrenceCount) || 1}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!dialogLoading && dialogCount > logs.length ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Showing the latest {logs.length} of {dialogCount} log lines.
                  </Alert>
                ) : null}
              </>
            ) : null}

            {!dialogError && dialogLoading && logs.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
      </>
    );
  }
}
